import { Button, Grid2 as Grid } from "@mui/material";
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useCompleteChasables } from "../../../hooks/useCompleteChasables";
import { useOutlook } from "../../../hooks/useOutlook";
import { Footer } from "../../../components/Footer";
import { BackButton } from "../../../components/BackButton";
import { CompleteButton } from "../../../components/CompleteButton";
import { ChaseButton } from "../../../components/ChaseButton";
import { ChasablesContext } from "../../../context/ChasablesContext";
import SkipButton from "../../../components/SkipButton/SkipButton";
import { useUngroup } from "../../../hooks/useUngroup";
import { ActionTypeEnum } from "../../../models/ChasableModels";
import { ContactEmailModel } from "../../../models/CustomerModels";
import { EmailTemplateResponse } from "../../../models/EmailTemplateModels";
import { DraftRecipientMode, hasOtherContacts } from "../../../utils/contactRecipients";

interface Props {
  selectedAll: boolean;
  completedInvoiceIds: string[];
  selectedInvoiceIds: string[];
  isChaseLoading: boolean;
  hasBeenChased: boolean;
  onChase(languageId?: string, recipientMode?: DraftRecipientMode): void;
  onComplete(completedInvoiceIds: string[]): void;
  onSkip?: () => void;
  invoicesGroupId?: string;
  contacts?: ContactEmailModel[];
  onOpenAttachPickerForDraft?(anchorEl: HTMLElement): void;
  onOpenAdjustTone?(anchorEl: HTMLElement): void;
  onAddOtherContactsToCc?(): void;
  onEmailTemplateClick?(template: EmailTemplateResponse, recipientMode?: DraftRecipientMode): void;
}

export function ChaseFooter({
  selectedAll,
  completedInvoiceIds,
  selectedInvoiceIds,
  isChaseLoading,
  hasBeenChased,
  onChase,
  onComplete,
  onSkip,
  invoicesGroupId,
  contacts,
  onOpenAttachPickerForDraft,
  onOpenAdjustTone,
  onAddOtherContactsToCc,
  onEmailTemplateClick,
}: Props) {
  const { isInReadMode } = useOutlook();
  const { chasables } = useContext(ChasablesContext);
  const { completeChasables, isLoading: isCompleteLoading } = useCompleteChasables();
  const { ungroup, isLoading: isUngroupLoading } = useUngroup();
  const navigate = useNavigate();

  const handleNavigation = (invoiceIds: string[]) => {
    if (!chasables) {
      return navigate("/");
    }

    const chasableActions = chasables.actions.filter(
      ({ type }) =>
        type === ActionTypeEnum.Chasable ||
        type === ActionTypeEnum.NewInvoice ||
        type === ActionTypeEnum.PromiseToPayBroken
    );

    const currentIndexes = invoiceIds
      .map((invoiceId) => chasableActions.findIndex((action) => action.externalId === invoiceId))
      .filter((index) => index !== -1 && index !== chasableActions.length - 1);

    if (!currentIndexes.length) {
      return navigate("/");
    }

    const minCurrentIndex = Math.min(...currentIndexes);
    let nextIndex = minCurrentIndex + 1;
    while (nextIndex < chasableActions.length) {
      if (!currentIndexes.includes(nextIndex)) break;

      nextIndex++;
    }

    if (nextIndex === chasableActions.length) {
      return navigate("/");
    }

    const nextChasable = chasableActions[nextIndex];

    if (!nextChasable) {
      return navigate("/");
    }

    return navigate(`/chasables/suggested/${nextChasable.id}`);
  };

  const handleBack = () => {
    if (chasables?.actions.some(({ externalId }) => completedInvoiceIds.includes(externalId))) {
      handleNavigation(completedInvoiceIds);
    } else {
      navigate(-1);
    }
  };

  const handleComplete = async (skipNavigation: boolean = false) => {
    try {
      if (invoicesGroupId) {
        await ungroup({ groupId: invoicesGroupId });
      }
      await completeChasables(selectedInvoiceIds);
      onComplete(selectedInvoiceIds);

      if (!skipNavigation) {
        handleNavigation(selectedInvoiceIds);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Footer>
      <Grid size={6}>
        {onSkip ? (
          <SkipButton
            handleSkipMenuItemClicked={onSkip}
            groupedInvoices={Boolean(invoicesGroupId)}
            isLoading={isCompleteLoading || isUngroupLoading}
            invoicesNumber={selectedInvoiceIds}
            handleMarkAsComplete={!hasBeenChased && !isInReadMode ? handleComplete : undefined}
          />
        ) : (
          <BackButton onClick={handleBack} />
        )}
      </Grid>
      <Grid size={6}>
        {hasBeenChased || isInReadMode ? (
          <CompleteButton
            onClick={() => handleComplete(isInReadMode && !selectedAll)}
            loading={isCompleteLoading || isChaseLoading}
            disabled={!selectedInvoiceIds.length || isCompleteLoading || isChaseLoading}
            onOpenAdjustTone={hasBeenChased ? onOpenAdjustTone : undefined}
            onAddOtherContactsToCc={
              hasBeenChased && onAddOtherContactsToCc && contacts && hasOtherContacts(contacts)
                ? onAddOtherContactsToCc
                : undefined
            }
          />
        ) : (
          <ChaseButton
            onClick={onChase}
            loading={isChaseLoading}
            disabled={!selectedInvoiceIds.length || isChaseLoading}
            contacts={contacts}
            invoiceCount={selectedInvoiceIds.length}
            onOpenAttachPickerForDraft={onOpenAttachPickerForDraft}
            onEmailTemplateClick={onEmailTemplateClick}
          />
        )}
      </Grid>
    </Footer>
  );
}

export function BackFooter() {
  return (
    <Footer>
      <BackButton />
    </Footer>
  );
}
